/**
 * squadActivity/schedule - the cadence, budget and failure table for the
 * squad activity read (dashboard social, Slice 2, D-4).
 *
 * PURE, so every rule is table-tested here rather than reachable only through
 * the hook's timers. The hook owns WHEN it asks; this file owns WHETHER it may.
 *
 * THERE IS NO PUSH. The backend serves `realtime_available: false` and no
 * socket message carries activity, so the badge is only ever as fresh as the
 * last read. The cadence is deliberately slow because every `throttle:N,1`
 * route on the backend shares ONE per-user bucket (plan F-2): the activity
 * read, the seen call, `me/economy`, the award calls and `me/completion` all
 * count against the same number. A local budget cannot see another tab or
 * those other calls, so it is a mitigation only; isolated backend keys are an
 * activation prerequisite (D-13).
 */

/** Passive reads while visible, unlocked and online. */
export const PASSIVE_INTERVAL_MS = 120000;
/** Mount, visibility and back-online reads need this much quiet since the last head. */
export const MIN_GAP_MS = 30000;
/** A cached head this young becomes the panel's snapshot without a read. */
export const PANEL_FRESH_MS = 30000;
/** A head older than this is re-read before `new updates` presents it (token TTL is 900s). */
export const HEAD_TOKEN_MAX_AGE_MS = 600000;
/** A gap this long between two head reads re-primes rather than rings. */
export const LONG_GAP_MS = 300000;
/** 429: the first pause, then every consecutive one after. */
export const THROTTLE_PAUSE_MS = [60000, 300000];
/** Transient and contract failures double the passive delay up to this. */
export const BACKOFF_CAP_MS = 600000;
/** A 404 `activity_disabled` scope may be probed again after this. */
export const DISABLED_RECHECK_MS = 600000;
/** The rolling request window. */
export const WINDOW_MS = 60000;
/** Passive reads are skipped at this many requests in the window. */
export const PASSIVE_PER_WINDOW = 2;
/** Every request is refused at this many requests in the window. */
export const TOTAL_PER_WINDOW = 6;
/** Every activity request carries this axios timeout. */
export const REQUEST_TIMEOUT_MS = 10000;
/** Pages the panel may load below its snapshot (`show earlier`). */
export const MAX_PAGES = 3;

/**
 * THE CONTRACT TABLE (plan Existing state E), as client classes.
 *
 * `off-disabled` is returned ONLY for a 404 carrying `activity_disabled`. Any
 * other 404 (a wrong path, a gateway, an old backend) is a `contract` error
 * that backs off like a transient failure: a broken route must never look
 * like the feature being switched off, or it would stay hidden and silent.
 *
 * @returns {"aborted"|"transient"|"off-disabled"|"contract"|"off-forbidden"|
 *   "stale-context"|"token-invalid"|"throttled"|"off-capacity"|
 *   "off-unauthenticated"}
 */
export const classifySquadActivityFailure = ({
  status = null,
  code = null,
  network = false,
  timeout = false,
  aborted = false,
} = {}) => {
  if (aborted) return "aborted";
  if (timeout || network || status === null || status === undefined) {
    return "transient";
  }
  switch (status) {
    case 401:
      return "off-unauthenticated";
    case 403:
      return "off-forbidden";
    case 404:
      return code === "activity_disabled" ? "off-disabled" : "contract";
    case 409:
      return code === "activity_context_changed" ? "stale-context" : "contract";
    case 422:
      return code === "activity_token_invalid" ? "token-invalid" : "contract";
    case 429:
      return "throttled";
    case 503:
      return code === "activity_initialization_capacity"
        ? "off-capacity"
        : "transient";
    default:
      return status >= 500 ? "transient" : "contract";
  }
};

/** Classes that turn the feature off for the scope. */
export const isOffKind = (kind) =>
  kind === "off-disabled" ||
  kind === "off-forbidden" ||
  kind === "off-capacity" ||
  kind === "off-unauthenticated";

/**
 * The next passive delay after `failures` consecutive transient or contract
 * failures: 120s, doubled per failure after the first, capped at 600s.
 */
export const nextPassiveDelayMs = (failures = 0) => {
  const n = Number.isInteger(failures) && failures > 0 ? failures : 0;
  if (n <= 1) return PASSIVE_INTERVAL_MS;
  return Math.min(PASSIVE_INTERVAL_MS * 2 ** (n - 1), BACKOFF_CAP_MS);
};

/** The pause after the `consecutive`-th 429 in a row: 60s, then 300s. */
export const throttlePauseMs = (consecutive = 1) =>
  consecutive <= 1 ? THROTTLE_PAUSE_MS[0] : THROTTLE_PAUSE_MS[1];

/** Requests logged inside the rolling window ending at `now`. */
export const requestsInWindow = (log = [], now = 0) =>
  (Array.isArray(log) ? log : []).filter(
    (entry) => entry && now - entry.at < WINDOW_MS,
  ).length;

/**
 * May ANY activity request go out right now?
 *
 * THE ECONOMY PENDING-RESULT POLL BLOCKS EVERYTHING, user initiated included:
 * it makes 15 reads in its first minute against the shared bucket, and
 * `me/economy` refuses at 20. A panel opened during the poll shows its cache
 * and sends nothing, not even a seen call.
 */
export const requestAllowed = ({
  log = [],
  now = 0,
  userInitiated = false,
  economyPollActive = false,
  pausedUntil = null,
  status = "idle",
} = {}) => {
  if (economyPollActive) return false;
  if (status === "off" || status === "scope-wait") return false;
  if (pausedUntil !== null && pausedUntil !== undefined && now < pausedUntil) {
    return false;
  }
  const inWindow = requestsInWindow(log, now);
  if (inWindow >= TOTAL_PER_WINDOW) return false;
  if (!userInitiated && inWindow >= PASSIVE_PER_WINDOW) return false;
  return true;
};

/**
 * May a PASSIVE read go out right now? Everything `requestAllowed` asks, plus
 * a visible, unlocked, online page. A status of `loading` means a read is in
 * flight, so a passive read waits for it.
 */
export const passiveAllowed = ({
  visible = true,
  locked = false,
  online = true,
  economyPollActive = false,
  pausedUntil = null,
  status = "idle",
  log = [],
  now = 0,
} = {}) => {
  if (!visible || locked || !online) return false;
  if (status !== "idle" && status !== "ready") return false;
  return requestAllowed({
    log,
    now,
    userInitiated: false,
    economyPollActive,
    pausedUntil,
    status,
  });
};

/** Is the held head young enough to present or to skip a read for? */
export const headIsFresh = (head, now, maxAgeMs = PANEL_FRESH_MS) =>
  !!head &&
  typeof head.receivedAt === "number" &&
  now - head.receivedAt <= maxAgeMs;

/** A 404 `activity_disabled` scope may be probed again only after 600s. */
export const disabledProbeAllowed = ({ offAt, now }) =>
  typeof offAt === "number" && now - offAt >= DISABLED_RECHECK_MS;

/**
 * THE IDS ONE PRESENTATION MAY ACKNOWLEDGE (D-8 rule 2).
 *
 * The presented events that are new, minus every id this tab has already
 * acknowledged. None at all when this exact token was already acknowledged:
 * reopening the panel on the same cached head must never send a second seen
 * call. The server accepts at most 5, and a page never holds more.
 */
export const idsToAcknowledge = ({
  events = [],
  token = null,
  acknowledged = { tokens: [], ids: [] },
  pending = { tokens: [], ids: [] },
} = {}) => {
  if (typeof token !== "string" || token.length === 0) return [];
  /* A seen call still in flight counts as acknowledged (G2): reopening the
     panel before it answers must not send the same ids again. */
  const tokens = [...(acknowledged?.tokens ?? []), ...(pending?.tokens ?? [])];
  const ids = [...(acknowledged?.ids ?? []), ...(pending?.ids ?? [])];
  if (tokens.includes(token)) return [];
  const out = [];
  (Array.isArray(events) ? events : []).forEach((event) => {
    if (!event || event.is_new !== true) return;
    const { id } = event;
    if (typeof id !== "string" || ids.includes(id) || out.includes(id)) return;
    out.push(id);
  });
  return out.slice(0, 5);
};
